import { Precision } from "../../../../types";
import floatToInt from "../../../../utils/floatToInt";
import {
  getEraDuration,
  getEraIsBackwards,
  getHoursInDayFromCalendar,
  getLeapDurationForMonth,
  getMonthsFromCalendar,
  getNormalDurationForMonth,
  getWeekdaysFromCalendar,
  isLeapYearFromCalendar} from "../selectors";
import {
  addYearsToDate,
  calculateComponentsForTimestamp,
  calculateTimestampForDate,
  calculateWeekdayForTimestamp,
  createImmutableDate,
  getFirstMonthOfYear
} from "./dateInternalCalculations";
import { precisionForTimespan } from "./timespanFunctions";

// ===================================================
// Exported Functions
// ===================================================

const dateByAddingSpan = (calendar, date, span) => {
  const months = getMonthsFromCalendar(calendar);
  const monthCount = months.count();
  const weekdayCount = getWeekdaysFromCalendar(calendar).count();
  const hoursInDay = getHoursInDayFromCalendar(calendar);

  const spanYears = span.years;
  const spanMonths = span.months;
  const spanWeeks = span.weeks;
  const spanDays = span.days;
  const spanHours = span.hours;
  const spanMinutes = span.minutes;
  const spanSeconds = span.seconds;

  const spanPrecision = precisionForTimespan(span);
  let era = date.era;
  let year = date.year;
  let month = date.month;
  let day = date.day;
  let hour = date.hour;
  let minute = date.minute;
  let second = date.second;
  let timestamp = date.timestamp;
  let precision = date.precision;

  let yearsToAdd = spanYears + floatToInt(spanMonths / monthCount);
  let monthsToAdd = spanMonths % monthCount;
  let secondsToAdd =
    (((spanWeeks * weekdayCount + spanDays) * hoursInDay + spanHours) * 60 + spanMinutes) * 60 + spanSeconds;

  if (yearsToAdd !== 0 || monthsToAdd !== 0) {
    month = month + monthsToAdd;
    if (month <= 0) {
      yearsToAdd--;
      month += monthCount;
    } else if (month > monthCount) {
      yearsToAdd++;
      month = ((month - 1) % monthCount) + 1;
    }

    if (yearsToAdd !== 0) {
      ({ era, year } = addYearsToDate(calendar, era, year, yearsToAdd));
    }

    let leapYear = isLeapYearFromCalendar(calendar, era, year);

    if (leapYear) {
      if (day > getLeapDurationForMonth(calendar, month)) {
        day = 1;
        month++;
        if (month <= monthCount) {
          while (getLeapDurationForMonth(calendar, month) <= 0) {
            month++;
            if (month > monthCount) {
              break;
            }
          }
        }
      }
    } else if (day > getNormalDurationForMonth(calendar, month)) {
      day = 1;
      month++;
      if (month <= monthCount) {
        while (getNormalDurationForMonth(calendar, month) <= 0) {
          month++;
          if (month > monthCount) {
            break;
          }
        }
      }
    }
    if (month > monthCount) {
      if (getEraIsBackwards(calendar, era)) {
        year--;
      } else {
        year++;
      }
      if (year > getEraDuration(calendar, era) || year <= 0) {
        era++;
        if (getEraIsBackwards(calendar, era)) {
          year = getEraDuration(calendar, era);
        } else {
          year = 1;
        }
      }
      month = getFirstMonthOfYear(calendar, era, year);
    }
    timestamp = calculateTimestampForDate(calendar, era, year, month, day, hour, minute, second);
  }

  if (secondsToAdd !== 0) {
    timestamp += secondsToAdd;
    ({ era, year, month, day, hour, minute, second } = calculateComponentsForTimestamp(calendar, timestamp));
  }
  const weekday = calculateWeekdayForTimestamp(calendar, timestamp);

  precision = Math.max(precision, spanPrecision);

  // precision should not be Week unless the weekday is zero
  if (precision === Precision.Week && weekday !== 0) {
    precision = Precision.Day;
  }

  return createImmutableDate(era, year, month, day, hour, minute, second, timestamp, weekday, precision);
};

export default dateByAddingSpan;
